import type { RootState } from '../redux/store'
import { CheckoutRules, ITextGroup } from '../interfaces'
import type { IRule } from '../graphQL/queries/checkoutSessionRule/types'
import { isHidePricesEnabled } from './hidePrices'

const LIMIT_PLACEHOLDER = /\{\{\s*limit\s*\}\}/g

/**
 * The cart-condition rule types whose server-side block should surface as a
 * cart-side alert.
 *
 * `CART_CONDITION_PENDING_USER_VALIDATION` is deliberately absent — the alcohol
 * flow handles that one through `getValidatorDialogsProps`'s own dialog, not
 * through this inline alert. The additional-measurement variants
 * (`CART_CONDITION_PENDING_MIN_ADDITIONAL_MEASUREMENT` and its max sibling)
 * are also absent today: no storefront authors them, there is no local
 * `AlertConstrains` entry to suppress alongside, and they are reachable only
 * through a server-side extension no tenant has turned on. Add them to this
 * set, their suffix to `RULE_TYPE_TO_SUFFIX`, and a template key to the seven
 * locales, in one commit, if a tenant does.
 */
export const CART_CONDITION_BLOCKING_RULE_TYPES: ReadonlySet<CheckoutRules> =
  new Set([
    CheckoutRules.CART_CONDITION_PENDING_MIN_AMOUNT,
    CheckoutRules.CART_CONDITION_PENDING_MAX_AMOUNT,
    CheckoutRules.CART_CONDITION_PENDING_MIN_QTY,
    CheckoutRules.CART_CONDITION_PENDING_MAX_QTY,
  ])

/**
 * The suffix each rule type carries under `texts.cart.cartConditionBlocked.*`.
 *
 * Static rather than derived from the enum string because the enum member and
 * the authoring surface are distinct contracts: a rename on one would silently
 * blank every storefront's copy if the suffix tracked it.
 */
const RULE_TYPE_TO_SUFFIX: Readonly<Record<string, string>> = {
  [CheckoutRules.CART_CONDITION_PENDING_MIN_AMOUNT]: 'minAmount',
  [CheckoutRules.CART_CONDITION_PENDING_MAX_AMOUNT]: 'maxAmount',
  [CheckoutRules.CART_CONDITION_PENDING_MIN_QTY]: 'minQty',
  [CheckoutRules.CART_CONDITION_PENDING_MAX_QTY]: 'maxQty',
}

const MONEY_RULE_TYPES: ReadonlySet<CheckoutRules> = new Set([
  CheckoutRules.CART_CONDITION_PENDING_MIN_AMOUNT,
  CheckoutRules.CART_CONDITION_PENDING_MAX_AMOUNT,
])

/**
 * The money audience knob, mirroring `OverwriteMinAmountMessageConfig.audience`.
 *
 * `'all'` (default): render the money-rule alert on every session.
 * `'hidePrices'`: render only when the viewing session has hidden prices.
 *
 * The qty rules are unaffected — quantities are not masked.
 */
export type CartConditionBlockedAlertAudience = 'all' | 'hidePrices'

export interface CartConditionBlockedAlertResult {
  show: boolean
  message: string
  ruleType?: CheckoutRules
  limit?: number
}

const EMPTY: CartConditionBlockedAlertResult = { show: false, message: '' }

interface ResolveCartConditionBlockedAlertArgs {
  rules: IRule[] | undefined
  cartTexts: ITextGroup | undefined
  hidePricesActive: boolean
  moneyAudience: CartConditionBlockedAlertAudience
}

const readTemplate = (
  cartTexts: ITextGroup | undefined,
  suffix: string,
): string => {
  const group = cartTexts?.cartConditionBlocked
  if (!group || typeof group !== 'object') return ''
  const value = (group as Record<string, unknown>)[suffix]
  return typeof value === 'string' ? value : ''
}

const readLimit = (rule: IRule): number | undefined => {
  const data = rule.data as unknown
  if (!data || typeof data !== 'object') return undefined
  const quantity = (data as { quantity?: unknown }).quantity
  return typeof quantity === 'number' && Number.isFinite(quantity)
    ? quantity
    : undefined
}

/**
 * The pure resolver — no store access, test-friendly.
 *
 * Returns `EMPTY` whenever the alert should stay silent: no server rule
 * qualifies, no copy authored for the matching suffix, or the money-audience
 * knob rejects this session.
 *
 * Iterates every qualifying rule in order of `rules` and returns the first one
 * that can **actually render** — a rule whose copy key is authored and whose
 * audience accepts the session. A rule that fails either gate is skipped
 * rather than silencing the whole surface. The server returns one rule per
 * rule type, so a mixed response (cart-condition + picker) still resolves to
 * the cart-condition one because picker rules are not in
 * `CART_CONDITION_BLOCKING_RULE_TYPES`.
 *
 * Why skip rather than return `EMPTY` at the first ineligible candidate:
 * review of #826 (COM-1213) called the opposite shape out. Authoring only
 * `minQty` copy with a blocking `minAmount` ahead of it in the response would
 * silence the surface rather than show the qty alert; and `audience:
 * 'hidePrices'` on a visible-price session would hide a trailing quantity
 * alert, contrary to this file's own stated qty-rule behaviour. Eligibility
 * is per candidate.
 */
export const resolveCartConditionBlockedAlert = ({
  rules,
  cartTexts,
  hidePricesActive,
  moneyAudience,
}: ResolveCartConditionBlockedAlertArgs): CartConditionBlockedAlertResult => {
  if (!rules || rules.length === 0) return EMPTY

  for (const rule of rules) {
    if (!rule.blocks || rule.checked) continue
    if (!CART_CONDITION_BLOCKING_RULE_TYPES.has(rule.ruleType)) continue

    if (
      MONEY_RULE_TYPES.has(rule.ruleType) &&
      moneyAudience === 'hidePrices' &&
      !hidePricesActive
    ) {
      continue
    }

    const suffix = RULE_TYPE_TO_SUFFIX[rule.ruleType]
    if (!suffix) continue

    const template = readTemplate(cartTexts, suffix)
    if (template === '') continue

    const limit = readLimit(rule)
    const message =
      limit === undefined
        ? template
        : template.replace(LIMIT_PLACEHOLDER, String(limit))

    return {
      show: true,
      message,
      ruleType: rule.ruleType,
      limit,
    }
  }

  return EMPTY
}

/**
 * State-reading entry the hook calls.
 *
 * Reads `lastAppliedRules` from `checkoutSlice` (written by
 * `useCheckoutRulePreflight` on `onCompleted`), the authored cart texts, the
 * hidePrices-active flag, and the money audience knob off
 * `config.options.cartConditionBlockedAlert`.
 */
export const getCartConditionBlockedAlert = (
  state: RootState,
): CartConditionBlockedAlertResult =>
  resolveCartConditionBlockedAlert({
    rules: state?.checkoutSlice?.lastAppliedRules,
    cartTexts: state?.defaultSlice?.config?.texts?.cart,
    hidePricesActive: isHidePricesEnabled(state),
    moneyAudience:
      state?.defaultSlice?.config?.options?.cartConditionBlockedAlert
        ?.audience ?? 'all',
  })
